// src/pages/QualifyStep.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/* -----------------------------
   Meta Pixel (inline helper)
----------------------------- */
const PIXEL_ID = "24921128320812912";
let _pixelLoaded = false;

function loadMetaPixel() {
  if (typeof window === "undefined") return;

  if (!_pixelLoaded) {
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = "https://connect.facebook.net/en_US/fbevents.js";
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script");

    _pixelLoaded = true;
    window.fbq("init", PIXEL_ID);
  }

  // Fire PageView for this page load
  window.fbq("track", "PageView");
}

function fbqTrackCustom(event, params = {}) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("trackCustom", event, params);
  }
}

/* -----------------------------
   Small Reveal helper (same feel)
----------------------------- */
function useInView(ref, options = { threshold: 0.15 }) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.unobserve(entry.target);
      }
    }, options);
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, options]);
  return inView;
}

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out motion-reduce:transition-none motion-reduce:transform-none ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* -----------------------------
   Component
----------------------------- */
export default function QualifyStep() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    hasIncome: "",
  });
  const [prevDebt, setPrevDebt] = useState("");
  const [errorFields, setErrorFields] = useState({});
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // On mount: load pixel + mark this page view with context
  useEffect(() => {
    loadMetaPixel();
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "ViewContent", {
        content_name: "QualifyStep",
        page_path: window.location.pathname,
        page_title: "Qualification",
      });
      // Also a custom event for analyst-friendly funnels
      fbqTrackCustom("QualificationViewed", {});
    }
  }, []);

  useEffect(() => {
    const data = (() => {
      try {
        return JSON.parse(localStorage.getItem("funnelData"));
      } catch {
        return null;
      }
    })();
    if (data?.debtAmount) setPrevDebt(data.debtAmount);
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrorFields((f) => ({ ...f, [name]: false }));
    setError("");
  }

  function isValidPhone(phone) {
    return /^\+?[0-9]{10,15}$/.test(phone);
  }
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
  function isValidName(name) {
    return /^[a-zA-Z\s'-]{2,}$/.test(name);
  }

  // Matches the new 4-tier format on Landing step, with fallbacks
  function convertDebtTierToNumber(tier) {
    switch (tier) {
      case "$1-$9,999":
      case "$1,000-$9,999":
      case "$1 - $9,999":
        return 5000;
      case "$10,000-$19,999":
      case "$10,000 - $19,999":
        return 15000;
      case "$20,000-$29,999":
      case "$20,000 - $29,999":
        return 25000;
      case "$30,000+":
      case "$30,000 +":
        return 35000;
      default:
        return null;
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const { firstName, lastName, phone, email, hasIncome } = form;
    let missing = {};

    if (!isValidName(firstName)) missing.firstName = true;
    if (!isValidName(lastName)) missing.lastName = true;
    if (!isValidPhone(phone)) missing.phone = true;
    if (!isValidEmail(email)) missing.email = true;
    if (!hasIncome) missing.hasIncome = true;

    setErrorFields(missing);
    if (Object.keys(missing).length > 0) {
      setError("Please correct the highlighted fields.");
      // Track WHAT failed (field names only; no PII)
      fbqTrackCustom("QualificationValidationFailed", {
        missing_fields: Object.keys(missing),
      });
      return;
    }

    const funnelData = (() => {
      try {
        return JSON.parse(localStorage.getItem("funnelData")) || {};
      } catch {
        return {};
      }
    })();
    const estimated_amount = convertDebtTierToNumber(funnelData.debtAmount);

    const lead = {
      source: "custom-api",
      first_name: firstName,
      last_name: lastName,
      email,
      phone: phone,
      street_address: funnelData.street || null,
      city: funnelData.city || null,
      state: funnelData.state || null,
      zip_code: funnelData.zip || null,
      estimated_amount,
      date_of_birth: funnelData.dob || null,
    };

    // Analyst-friendly: user finished qualification form (no PII values sent)
    fbqTrackCustom("QualificationSubmitted", {
      debt_tier: funnelData.debtAmount || null,
      has_income: hasIncome || null,
    });

    setIsSubmitting(true);
    try {
      const response = await fetch(
        "https://tmliqznzevbcakrsutdc.supabase.co/functions/v1/lead-intake",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer sk_q4v6E28q9WPbYgdImORE3litV4REuUr",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(lead),
        }
      );

      if (!response.ok) {
        setError("There was a problem submitting your request. Please try again.");
        setIsSubmitting(false);
        // Track submission failure (no PII)
        fbqTrackCustom("QualificationSubmitFailed", {
          status: response.status,
        });
        return;
      }

      localStorage.removeItem("funnelData");

      // Optional: track a pre-success marker if you want
      fbqTrackCustom("QualificationSubmitSucceeded", {
        debt_tier: funnelData.debtAmount || null,
      });

      navigate("/success");
    } catch {
      setError("Something went wrong. Please try again later.");
      setIsSubmitting(false);
      fbqTrackCustom("QualificationSubmitError", {});
    }
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      {/* Submitting Overlay */}
      {isSubmitting && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
          aria-live="assertive"
          role="alert"
        >
          <div className="bg-white rounded-2xl shadow-xl p-6 w-[90%] max-w-sm text-center">
            <div className="mx-auto mb-3 w-10 h-10 rounded-full border-4 border-[#0057B8]/30 border-t-[#0057B8] animate-spin" />
            <h4 className="text-[#0f172a] font-semibold">Submitting your request…</h4>
            <p className="text-gray-600 text-sm mt-1">
              Please hold on while we process your information.
            </p>
          </div>
        </div>
      )}

      <main className="w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-10">
        {/* Heading */}
        <Reveal className="text-center">
          <h2 className="font-extrabold text-[24px] sm:text-[32px] leading-tight text-[#0f172a]">
            You’re on the Right Track—Keep Going!
          </h2>
          {prevDebt && (
            <div className="mt-3 inline-flex items-center gap-2 bg-white border border-[#e9ecf3] text-[#0b3d6e] rounded-full px-3.5 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold">
              <span className="opacity-70">Loan Amount:</span>
              <span className="text-[#0057B8]">{prevDebt}</span>
            </div>
          )}
        </Reveal>

        <div className="h-px bg-[#e9ecf3] my-6 sm:my-8" />

        {/* Error summary */}
        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 text-sm sm:text-base rounded-xl px-4 py-3 mb-4"
            role="alert"
            aria-live="polite"
          >
            {error}
          </div>
        )}

        {/* Form card */}
        <Reveal>
          <form
            onSubmit={handleSubmit}
            noValidate
            className={`bg-white rounded-2xl border border-[#e9ecf3] shadow-sm p-5 sm:p-8 ${
              isSubmitting ? "pointer-events-none opacity-90" : ""
            }`}
            aria-label="Qualification Form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              {/* First Name */}
              <label className="block">
                <span className="text-[11px] sm:text-xs font-semibold text-[#0f172a]">
                  First Name
                </span>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  autoComplete="given-name"
                  inputMode="text"
                  aria-invalid={!!errorFields.firstName}
                  aria-describedby={errorFields.firstName ? "err-firstName" : undefined}
                  disabled={isSubmitting}
                  className={`w-full mt-1 px-3 py-3 rounded-xl border text-sm sm:text-base ${
                    errorFields.firstName ? "border-red-500" : "border-[#dfe4ee]"
                  } focus:outline-none focus:ring-2 focus:ring-[#0057B8] bg-white disabled:opacity-60`}
                />
                {errorFields.firstName && (
                  <span id="err-firstName" className="text-xs text-red-600 mt-1 block">
                    Enter a valid first name.
                  </span>
                )}
              </label>

              {/* Last Name */}
              <label className="block">
                <span className="text-[11px] sm:text-xs font-semibold text-[#0f172a]">
                  Last Name
                </span>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                  inputMode="text"
                  aria-invalid={!!errorFields.lastName}
                  aria-describedby={errorFields.lastName ? "err-lastName" : undefined}
                  disabled={isSubmitting}
                  className={`w-full mt-1 px-3 py-3 rounded-xl border text-sm sm:text-base ${
                    errorFields.lastName ? "border-red-500" : "border-[#dfe4ee]"
                  } focus:outline-none focus:ring-2 focus:ring-[#0057B8] bg-white disabled:opacity-60`}
                />
                {errorFields.lastName && (
                  <span id="err-lastName" className="text-xs text-red-600 mt-1 block">
                    Enter a valid last name.
                  </span>
                )}
              </label>

              {/* Phone */}
              <label className="block">
                <span className="text-[11px] sm:text-xs font-semibold text-[#0f172a]">
                  Phone Number
                </span>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                  inputMode="tel"
                  aria-invalid={!!errorFields.phone}
                  aria-describedby={errorFields.phone ? "err-phone" : "hint-phone"}
                  disabled={isSubmitting}
                  className={`w-full mt-1 px-3 py-3 rounded-xl border text-sm sm:text-base ${
                    errorFields.phone ? "border-red-500" : "border-[#dfe4ee]"
                  } focus:outline-none focus:ring-2 focus:ring-[#0057B8] bg-white disabled:opacity-60`}
                />
                {!errorFields.phone && (
                  <span id="hint-phone" className="text-[11px] text-gray-500 mt-1 block"></span>
                )}
                {errorFields.phone && (
                  <span id="err-phone" className="text-xs text-red-600 mt-1 block">
                    Enter a valid phone number.
                  </span>
                )}
              </label>

              {/* Email */}
              <label className="block">
                <span className="text-[11px] sm:text-xs font-semibold text-[#0f172a]">
                  Email Address
                </span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  inputMode="email"
                  aria-invalid={!!errorFields.email}
                  aria-describedby={errorFields.email ? "err-email" : undefined}
                  disabled={isSubmitting}
                  className={`w-full mt-1 px-3 py-3 rounded-xl border text-sm sm:text-base ${
                    errorFields.email ? "border-red-500" : "border-[#dfe4ee]"
                  } focus:outline-none focus:ring-2 focus:ring-[#0057B8] bg-white disabled:opacity-60`}
                />
                {errorFields.email && (
                  <span id="err-email" className="text-xs text-red-600 mt-1 block">
                    Enter a valid email address.
                  </span>
                )}
              </label>

              {/* Has Income */}
              <label className="block md:col-span-2">
                <span className="text-[11px] sm:text-xs font-semibold text-[#0f172a]">
                  Do you have a source of income?
                </span>
                <select
                  name="hasIncome"
                  value={form.hasIncome}
                  onChange={handleChange}
                  aria-invalid={!!errorFields.hasIncome}
                  aria-describedby={errorFields.hasIncome ? "err-income" : undefined}
                  disabled={isSubmitting}
                  className={`w-full mt-1 px-3 py-3 rounded-xl border text-sm sm:text-base ${
                    errorFields.hasIncome ? "border-red-500" : "border-[#dfe4ee]"
                  } focus:outline-none focus:ring-2 focus:ring-[#0057B8] bg-white disabled:opacity-60`}
                >
                  <option value="">-- Select --</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                {errorFields.hasIncome && (
                  <span id="err-income" className="text-xs text-red-600 mt-1 block">
                    Please select your income status.
                  </span>
                )}
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#0057B8] hover:bg-[#003e7e] text-white font-bold py-3 rounded-xl mt-6 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              aria-live="polite"
            >
              {isSubmitting ? (
                <>
                  <span className="w-5 h-5 rounded-full border-2 border-white/50 border-t-white animate-spin" />
                  Submitting…
                </>
              ) : (
                "SUBMIT"
              )}
            </button>

            {/* Consent Notice (centered) */}
            <p className="mt-4 text-xs sm:text-sm text-gray-600 leading-relaxed text-center">
              By clicking <strong>Submit</strong>, I authorize{" "}
              <strong>Amend Finance LLC</strong> to contact me to provide
              debt-related options and recommendations. I agree to the site’s{" "}
              <strong>Terms of Service</strong> and acknowledge the{" "}
              <strong>Privacy Policy</strong>. I consent to receive emails,
              automated calls, prerecorded messages, artificial intelligence
              calls, and SMS messages, even if my number is on a Do Not Call
              list. I understand that messages and data rates may apply, and
              that I may reply STOP to opt-out of future messages or reply HELP
              for message help. Message frequency may vary based on interaction.
              Consent is not required for purchase. Calls may be recorded or
              monitored for quality. Refer to our <strong>Privacy Policy</strong> for
              more information.
            </p>
          </form>
        </Reveal>
      </main>
    </div>
  );
}
